import { Callout, ICalloutProps, IDetailsRowProps, MessageBarType } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import {
  BaseDialog,
  Button,
  buttonStylesDanger,
  buttonStylesGhost,
  calloutContentStylesPopover,
  CommonStyles,
  debounce,
  defaultPalette,
  detailsHeaderStylesBasic,
  DetailsList,
  detailsListStylesBasic,
  detailsListStylesCard,
  EmptyMessage,
  FontWeights,
  getItemsWithEmptyItemsOfNextPage,
  highlightRowBorder,
  IColumnWithDataType,
  IconButton,
  iconButtonStylesNoBG,
  IDetailsListProps,
  IItemProps,
  Item,
  itemStylesTruncatedTag,
  Link,
  linkStylesGhost,
  ListRow,
  listRowStylesThin,
  Loader,
  loaderStylesSpinnerXLarge,
  MessageBar,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  JobProgressOutput,
  JobProgressOutputProgress,
  ListableFeatureSetDraft,
  ListableFeatureSetDraftFeatureSetDraftStatus,
  ListFeatureSetDraftsRequest,
  ListFeatureSetDraftsResponse,
  Project,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { CommonImageSrc, ContentsTitle, defaultPageSize } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { ListCellImage } from '../ListCellImage/ListCellImage';
import {
  featureSetDraftListStylesDefault,
  IFeatureSetDraftListClassNames,
  IFeatureSetDraftListStyles,
} from './FeatureSetDraftList.styles';

enum CalloutType {
  Progress = 'Progress',
  Error = 'Error',
}

const statusLabelColorMap = new Map([
  [
    ListableFeatureSetDraftFeatureSetDraftStatus.FEATURE_SET_DRAFT_STATUS_FAILED,
    { label: 'Failed', backgroundColor: defaultPalette.red100 },
  ],
  [
    ListableFeatureSetDraftFeatureSetDraftStatus.FEATURE_SET_DRAFT_STATUS_FINISHED,
    { label: 'Finished', backgroundColor: defaultPalette.blue100 },
  ],
  [
    ListableFeatureSetDraftFeatureSetDraftStatus.FEATURE_SET_DRAFT_STATUS_IN_PROGRESS,
    { label: 'In Progress', backgroundColor: defaultPalette.green100 },
  ],
  [
    ListableFeatureSetDraftFeatureSetDraftStatus.FEATURE_SET_DRAFT_STATUS_UNSPECIFIED,
    { label: 'Unspecified', backgroundColor: defaultPalette.yellow100 },
  ],
]);

// move to ui-kit
export const onRenderTagColumn = (
  item: { [key: string]: any },
  title: string,
  field: string,
  backgroundColor = '',
  hideHeader = false,
  itemProps?: IItemProps<{ [key: string]: any }>
) => {
  const _itemProps: IItemProps<{ [key: string]: any }> = {
    styles: itemStylesTruncatedTag,
    idField: field,
    styleField: 'style',
    labelField: field,
    titleField: field,
    data: {
      ...item,
      style: { backgroundColor: backgroundColor },
    },
    ...itemProps,
  };
  return (
    <div style={{ width: '100%', ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
      {!hideHeader && (
        <div
          style={{
            fontWeight: FontWeights.semiBold,
            paddingBottom: 4,
          }}
        >
          {title}
        </div>
      )}
      <div style={{ height: 20 }}>
        <Item {..._itemProps} />
      </div>
    </div>
  );
};

const getStatusName = (status: ListableFeatureSetDraftFeatureSetDraftStatus) => {
  const str = status.split('_');
  return str[str.length - 1];
};

const getColumns = (
  isCardView: boolean,
  onContinue: (item: ListableFeatureSetDraft) => void,
  onDelete: (item: ListableFeatureSetDraft) => void,
  onShowProgress: (target: HTMLDivElement, jobId: string) => void,
  onShowError: (target: HTMLDivElement, job: ListableFeatureSetDraft) => void
): IColumnWithDataType[] => {
  const columns: IColumnWithDataType[] = [];
  if (isCardView) {
    columns.push({
      key: 'featureSetName',
      name: 'Feature Set Name',
      fieldName: 'description',
      minWidth: 200,
      data: {
        headerFieldName: 'name',
        listCellProps: {
          emptyMessage: 'No Description',
          onRenderPrefix: (item) =>
            item.name?.toLowerCase().includes('h2o') ? (
              <ListCellImage imageSrc={CommonImageSrc.logo512} />
            ) : (
              <ListCellImage iconName="FormLibraryMirrored" />
            ),
        },
      },
    });
  } else {
    columns.push(
      {
        key: 'featureSetName',
        name: 'Feature Set Name',
        fieldName: 'name',
        minWidth: 100,
        maxWidth: 150,
      },
      {
        key: 'description',
        name: 'Description',
        fieldName: 'description',
        minWidth: 200,
        data: {
          listCellProps: { emptyMessage: 'No Description' },
        },
      }
    );
  }

  columns.push(
    {
      key: 'project',
      name: 'Project Name',
      fieldName: 'projectName',
      minWidth: 150,
    },
    {
      key: 'owner',
      name: 'Owner',
      fieldName: 'ownerName',
      minWidth: 120,
    },
    {
      key: 'expirationDate',
      name: 'Expiration Date',
      fieldName: 'expirationDateString',
      minWidth: 120,
      data: {
        listCellProps: { emptyMessage: '-' },
      },
    },
    {
      key: 'status',
      name: 'Status',
      fieldName: 'status',
      minWidth: 140,
      maxWidth: 140,
      onRender: (item) => {
        const backgroundColor = statusLabelColorMap.get(item.status)?.backgroundColor;
        const titleField =
          item.status === ListableFeatureSetDraftFeatureSetDraftStatus.FEATURE_SET_DRAFT_STATUS_FAILED
            ? 'errorMessage'
            : 'status';
        return onRenderTagColumn(item, 'Status', 'status', '', true, {
          titleField,
          hasTooltip: titleField === 'errorMessage',
          data: {
            ...item,
            status: getStatusName(item.status),
            style: { backgroundColor },
          },
        });
      },
    },
    {
      key: 'buttons',
      name: 'Actions',
      minWidth: 385,
      onRender: (item) => {
        const disabledContinue =
          item.status === ListableFeatureSetDraftFeatureSetDraftStatus.FEATURE_SET_DRAFT_STATUS_FAILED ||
          item.status === ListableFeatureSetDraftFeatureSetDraftStatus.FEATURE_SET_DRAFT_STATUS_IN_PROGRESS;
        const disabledDelete =
          item.status === ListableFeatureSetDraftFeatureSetDraftStatus.FEATURE_SET_DRAFT_STATUS_UNSPECIFIED;
        return (
          <div
            style={{
              ...(CommonStyles.flexCenterVertically as CSSProperties),
              width: '100%',
              textAlign: 'right',
            }}
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                text="Show Progress"
                styles={buttonStylesGhost}
                onClick={(e) => onShowProgress(e.currentTarget as HTMLDivElement, item.jobId)}
              />
              {item.errorMessage ? (
                <Link
                  styles={linkStylesGhost}
                  onClick={(e: React.MouseEvent) => {
                    onShowError(e.currentTarget as HTMLDivElement, item);
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  Show Error
                </Link>
              ) : (
                <Button
                  text="Continue"
                  styles={buttonStylesGhost}
                  disabled={disabledContinue}
                  onClick={() => onContinue(item)}
                />
              )}
              <Button
                text="Delete"
                styles={[buttonStylesDanger]}
                disabled={disabledDelete}
                onClick={() => onDelete(item)}
              />
            </div>
          </div>
        );
      },
    }
  );

  return columns;
};

interface FeatureSetDraftWithInfo extends ListableFeatureSetDraft {
  ownerName?: string;
  expirationDateString?: string;
}

export interface FeatureSetDraftListData {
  items: FeatureSetDraftWithInfo[];
  pageToken?: string;
  pageSize?: number;
}

const convertFeatureSetToFeatureSetWithUserNames = (item: ListableFeatureSetDraft): FeatureSetDraftWithInfo => ({
  ...item,
  expirationDateString: item.expirationDate ? new Date(item.expirationDate).toLocaleString() : '-',
  ownerName: item.owner?.name,
});

interface IFeatureSetDraftListProps {
  isCardView?: boolean;
  searchText?: string;
  project?: Project;
  reload?: boolean;
  refreshTime?: number;
  onUpdateData?: (count: number, pageSize: number, pageToken: string, name: ContentsTitle) => void;
  onContinue: (item: FeatureSetDraftWithInfo) => void;
  onLoading: (loading: boolean) => void;
}

export const FeatureSetDraftList = ({
  isCardView = true,
  reload,
  refreshTime,
  onUpdateData,
  onContinue,
  onLoading,
}: IFeatureSetDraftListProps) => {
  const [data, setData] = useState<FeatureSetDraftListData>({
    items: [],
  });
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [progress, setProgress] = useState<JobProgressOutputProgress[]>([]);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [showCallout, setShowCallout] = useState(false);
  const refMeta = useRef<{
    pageSize: number;
    isLoadingPage: boolean;
    calloutBtnEl?: HTMLElement;
    selectedItem?: FeatureSetDraftWithInfo;
    refreshIntervalId?: number;
    calloutType?: CalloutType;
  }>({
    pageSize: defaultPageSize,
    isLoadingPage: false,
  });
  const { addToast } = useToast();
  const load = useCallback(
    async ({ items, pageToken }: FeatureSetDraftListData = { items: [] }, hasLoading = true) => {
      hasLoading && setLoadingMessage(`Loading feature set drafts...`);
      onLoading(true);
      try {
        const pageSize = refMeta.current.pageSize;
        const req: ListFeatureSetDraftsRequest = {
          pageSize,
          pageToken,
        };
        const { drafts = [], nextPageToken = '' }: ListFeatureSetDraftsResponse = await fsApi(
          CoreService.ListFeatureSetDrafts,
          req
        );
        const newItems = getItemsWithEmptyItemsOfNextPage(
          items,
          drafts,
          pageSize,
          convertFeatureSetToFeatureSetWithUserNames
        );
        const newData = {
          items: newItems,
          pageToken: nextPageToken,
          pageSize,
        };
        setData({
          ...newData,
        });
        onUpdateData &&
          onUpdateData(newData.items.length, newData.pageSize, newData.pageToken, ContentsTitle.ongoingRegistration);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading feature set drafts: `));
      } finally {
        if (hasLoading) setLoadingMessage('');
        refMeta.current.isLoadingPage = false;
        onLoading(false);
      }
    },
    [addToast, onUpdateData]
  );

  const deleteItem = useCallback(async (item?: FeatureSetDraftWithInfo) => {
    if (!item) return;
    setLoadingMessage(`Deleting ${item.name}...`);
    try {
      await fsApi(CoreService.DeleteFeatureSetDraft, { featureSetDraftId: item.featureSetDraftId });
    } catch (err) {
      addToast(getErrorMessageBarMessage(err, `An error has occurred while deleting feature set draft: `));
    } finally {
      setLoadingMessage('');
      load();
    }
  }, []);

  const onLoad = useMemo(() => debounce(300, load), [load]);
  const onDelete = useCallback((item: FeatureSetDraftWithInfo) => {
    refMeta.current.selectedItem = item;
    toggleHideDialog();
  }, []);
  const loadProgress = useCallback(
    async (jobId?: string) => {
      setLoadingProgress(true);
      try {
        const { progress = [] }: JobProgressOutput = await fsApi(CoreService.GetJobProgress, {
          jobId,
          nextIndex: 0,
        });
        setProgress(progress);
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading the progress: `));
      } finally {
        setLoadingProgress(false);
      }
    },
    [addToast]
  );
  const onShowProgress = useCallback(
    (target: HTMLDivElement, jobId: string) => {
      refMeta.current.calloutBtnEl = target;
      refMeta.current.calloutType = CalloutType.Progress;
      setShowCallout(true);
      loadProgress(jobId);
    },
    [loadProgress]
  );
  const onShowError = useCallback((target: HTMLDivElement, item: FeatureSetDraftWithInfo) => {
    if (item.errorMessage) {
      refMeta.current.calloutBtnEl = target;
      refMeta.current.selectedItem = item;
      refMeta.current.calloutType = CalloutType.Error;
      setShowCallout(true);
    }
  }, []);
  const renderCallout = useCallback(() => {
    if (refMeta.current.calloutType === CalloutType.Progress) {
      return loadingProgress ? (
        <Loader label="Loading progress..." />
      ) : (
        <div>
          {progress && progress.length > 0
            ? progress.map(({ message, timestamp }, i) => <div key={i}>{`${timestamp}: ${message}`}</div>)
            : 'Data not present yet'}
        </div>
      );
    } else {
      return (
        <div style={{ whiteSpace: 'pre-line' }} data-test="job-error-callout">
          {refMeta.current.selectedItem?.errorMessage}
        </div>
      );
    }
  }, [loadingProgress, progress, showCallout]);
  const calloutProps: ICalloutProps = {
    target: refMeta.current.calloutBtnEl,
    styles: calloutContentStylesPopover,
    onDismiss: () => {
      setShowCallout(false);
    },
  };
  const detailsListProps: IDetailsListProps = {
    styles: isCardView ? detailsListStylesCard : detailsListStylesBasic,
    headerStyles: detailsHeaderStylesBasic,
    columns: getColumns(isCardView, onContinue, onDelete, onShowProgress, onShowError),
    isHeaderVisible: !isCardView,
    items: data.items,
    cellStyleProps: {
      cellExtraRightPadding: 0,
      cellLeftPadding: 0,
      cellRightPadding: 0,
    },
    onRenderRow: (props?: IDetailsRowProps) => {
      const { item, columns = [] } = props || {};
      if (item.isNotLoaded && !refMeta.current.isLoadingPage) {
        refMeta.current.isLoadingPage = true;
        onLoad(data, false);
      }
      return (
        <ListRow
          styles={isCardView ? undefined : listRowStylesThin}
          hideHeader={!isCardView}
          data={item}
          columns={columns}
          loading={item.isNotLoaded}
          onMouseEnter={isCardView ? undefined : (e) => highlightRowBorder(e)}
          onMouseLeave={isCardView ? undefined : (e) => highlightRowBorder(e, false)}
        />
      );
    },
  };
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    reload && load();
  }, [reload]);
  useEffect(() => {
    if (refMeta.current.refreshIntervalId !== undefined) {
      clearInterval(refMeta.current.refreshIntervalId);
      refMeta.current.refreshIntervalId = undefined;
    }
    if (refreshTime) {
      load();
      refMeta.current.refreshIntervalId = window.setInterval(() => load(), refreshTime * 1000);
    }
    return () => {
      if (refMeta.current.refreshIntervalId !== undefined) {
        clearInterval(refMeta.current.refreshIntervalId);
        refMeta.current.refreshIntervalId = undefined;
      }
    };
  }, [refreshTime]);
  const classNames = useClassNames<IFeatureSetDraftListStyles, IFeatureSetDraftListClassNames>(
    'FeatureSetDraftList',
    featureSetDraftListStylesDefault
  );
  return (
    <div className={classNames.root} data-test="FeatureSetDraftList">
      {loadingMessage ? (
        <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
      ) : isCardView ? (
        <div className={classNames.cardView} data-is-scrollable="true" data-test="details-list-card-view">
          {data.items.length === 0 ? (
            <EmptyMessage message="No Feature Sets Available" />
          ) : (
            <DetailsList {...detailsListProps} />
          )}
        </div>
      ) : (
        <div className={classNames.listView}>
          <div className={classNames.listViewWrap} data-is-scrollable="true" data-test="details-list-list-view">
            {data.items.length === 0 ? (
              <EmptyMessage message="No Feature Sets Available" />
            ) : (
              <DetailsList {...detailsListProps} />
            )}
          </div>
        </div>
      )}
      {showCallout && (
        <Callout {...calloutProps}>
          <div className={classNames.calloutContents}>
            <div className={classNames.calloutHeader}>
              <span>{refMeta.current.calloutType}</span>
              <IconButton
                styles={iconButtonStylesNoBG}
                iconName="Clear"
                onClick={() => setShowCallout(false)}
                data-test="owner-list-close-button"
              />
            </div>
            <div className={classNames.calloutBody}>{renderCallout()}</div>
          </div>
        </Callout>
      )}
      <BaseDialog
        hidden={hideDialog}
        onDismiss={toggleHideDialog}
        title={`Delete ${refMeta.current.selectedItem?.name}`}
        content={
          <MessageBar messageBarType={MessageBarType.error}>
            Are you sure you want to delete <strong>{refMeta.current.selectedItem?.name}</strong>?
          </MessageBar>
        }
        footer={
          <>
            <Link styles={[linkStylesGhost, { root: { marginRight: 8 } }]} onClick={toggleHideDialog}>
              Cancel
            </Link>
            <Link
              styles={[
                linkStylesGhost,
                {
                  root: {
                    borderColor: defaultPalette.red500,
                    backgroundColor: defaultPalette.red500,
                    color: defaultPalette.white,
                  },
                },
              ]}
              onClick={() => {
                toggleHideDialog();
                deleteItem(refMeta.current.selectedItem);
              }}
            >{`Delete ${refMeta.current.selectedItem?.name}`}</Link>
          </>
        }
      ></BaseDialog>
    </div>
  );
};
